<template>
    <div class="scheduled-meetings-card floating-widget">
        <div class="card-header">
            <div class="card-title-group">
                <h2 class="card-title">Scheduled Meetings</h2>
                <div v-if="!isGoogleConfigured && !isLoading" class="google-not-configured-badge" title="Set GOOGLE_SERVICE_ACCOUNT_JSON to enable live calendar sync">
                    <i class="fas fa-exclamation-circle"></i> Google Calendar not configured
                </div>
            </div>
            <div class="controls">
                <div class="control-group">
                    <label for="sort-by">Sort By:</label>
                    <select id="sort-by" v-model="sortBy" class="form-select">
                        <option value="date_desc">Date (Newest)</option>
                        <option value="date_asc">Date (Oldest)</option>
                        <option value="name">Name</option>
                    </select>
                </div>
                <div class="control-group">
                    <label for="rows-per-page">Rows per page:</label>
                    <select id="rows-per-page" v-model="pagination.pageSize" class="form-select">
                        <option>5</option>
                        <option>10</option>
                        <option>20</option>
                    </select>
                </div>

                <div class="pagination">
                    <button @click="prevPage" :disabled="pagination.currentPage === 1" class="pagination-arrow">
                        <i class="fas fa-chevron-left"></i>
                    </button>
                    <span class="pagination-label">
                        <strong>{{ paginationRange }}</strong> of {{ paginationTotal }}
                    </span>
                    <button @click="nextPage" :disabled="pagination.currentPage === totalPages" class="pagination-arrow">
                        <i class="fas fa-chevron-right"></i>
                    </button>
                </div>
            </div>
        </div>

        <!-- Google Calendar setup banner -->
        <div v-if="!isGoogleConfigured && !isLoading" class="setup-banner">
            <i class="fab fa-google"></i>
            <div>
                <strong>Google Calendar Integration Ready</strong>
                <p>To activate live meeting sync: create a Google Cloud project, add a Service Account with domain-wide delegation, then set the <code>GOOGLE_SERVICE_ACCOUNT_JSON</code> environment variable on the server. See <code>services/googleCalendarService.go</code> for full setup instructions.</p>
            </div>
        </div>

        <div class="meetings-list">
            <div v-if="isLoading" class="loading-state">
                <i class="fas fa-spinner fa-spin"></i> Loading meetings...
            </div>
            <div v-else-if="paginatedMeetings.length === 0" class="empty-state">
                <i class="far fa-calendar-times" style="font-size:2rem; display:block; margin-bottom:8px; color:#dee2e6;"></i>
                No scheduled meetings found.
            </div>
            <div v-else v-for="meeting in paginatedMeetings" :key="meeting.id" class="meeting-row" :class="{ 'cancelled': meeting.status === 'cancelled' }">
                <div class="meeting-date">
                    <div class="date-formatted">{{ formatDate(meeting.meeting_datetime) }}</div>
                    <div class="time-formatted">{{ formatTime(meeting.meeting_datetime) }}</div>
                </div>
                <div class="meeting-info">
                    <div class="meeting-name">{{ meeting.client_name }}</div>
                    <div class="meeting-contact">{{ meeting.client_phone || '—' }}</div>
                    <div class="meeting-contact">{{ meeting.client_email }}</div>
                </div>
                <div class="meeting-bdm">
                    <div class="bdm-label">BDM</div>
                    <div class="bdm-name">{{ meeting.bdm_name || '—' }}</div>
                </div>
                <div class="meeting-status">
                    <span class="status-badge" :class="meeting.status">{{ meeting.status }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ScheduledMeetingsWidget',
    data() {
        return {
            isLoading: true,
            isGoogleConfigured: false,
            meetings: [],
            sortBy: 'date_asc',
            pagination: {
                currentPage: 1,
                pageSize: 5,
            },
        };
    },
    computed: {
        sortedMeetings() {
            const sorted = [...this.meetings];
            sorted.sort((a, b) => {
                if (this.sortBy === 'name') return a.client_name.localeCompare(b.client_name);
                if (this.sortBy === 'date_desc') return new Date(b.meeting_datetime) - new Date(a.meeting_datetime);
                if (this.sortBy === 'date_asc') return new Date(a.meeting_datetime) - new Date(b.meeting_datetime);
                return 0;
            });
            return sorted;
        },
        paginatedMeetings() {
            const start = (this.pagination.currentPage - 1) * this.pagination.pageSize;
            return this.sortedMeetings.slice(start, start + this.pagination.pageSize);
        },
        totalPages() {
            return Math.max(1, Math.ceil(this.sortedMeetings.length / this.pagination.pageSize));
        },
        paginationRange() {
            const total = this.sortedMeetings.length;
            if (total === 0) return '0';
            const start = (this.pagination.currentPage - 1) * this.pagination.pageSize + 1;
            const end = Math.min(start + this.pagination.pageSize - 1, total);
            return `${start}-${end}`;
        },
        paginationTotal() {
            return this.sortedMeetings.length;
        },
    },
    watch: {
        'pagination.pageSize'() {
            this.pagination.currentPage = 1;
        }
    },
    methods: {
        formatDate(dateString) {
            const date = new Date(dateString);
            const year = date.getFullYear();
            const day = String(date.getDate()).padStart(2, '0');
            const month = date.toLocaleString('en-US', { month: 'short' });
            return `${year}-${day}-${month}`;
        },
        formatTime(dateString) {
            return new Date(dateString).toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
            });
        },
        nextPage() {
            if (this.pagination.currentPage < this.totalPages) this.pagination.currentPage++;
        },
        prevPage() {
            if (this.pagination.currentPage > 1) this.pagination.currentPage--;
        },
        async fetchMeetings() {
            this.isLoading = true;
            try {
                const response = await this.$axios.get('/bdm-bookings');
                if (response.data.success) {
                    this.meetings = response.data.bookings || [];
                    this.isGoogleConfigured = response.data.is_google_configured || false;
                }
            } catch (error) {
                console.error('Failed to fetch BDM meetings:', error);
                this.meetings = [];
            } finally {
                this.isLoading = false;
            }
        },
    },
    created() {
        this.fetchMeetings();
    },
};
</script>

<style scoped>
.scheduled-meetings-card {
    background-color: white;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    padding: 0;
    font-family: 'Roboto', sans-serif;
}

.floating-widget {
    position: relative;
    width: 100%;
    max-width: 100%;
    flex-grow: 1;
    z-index: 10;
}

/* Header */
.card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 24px;
    border-bottom: 1px solid #e9ecef;
    flex-wrap: wrap;
    gap: 12px;
}

.card-title-group {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}

.card-title {
    font-size: 1.25rem;
    font-weight: 600;
    margin: 0;
}

/* Google not configured badge */
.google-not-configured-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #fff3cd;
    color: #856404;
    border: 1px solid #ffc107;
    border-radius: 20px;
    padding: 3px 12px;
    font-size: 0.78rem;
    font-weight: 500;
    cursor: help;
}

/* Setup banner */
.setup-banner {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    background: #f0f9ff;
    border-left: 4px solid #00a0c1;
    padding: 14px 24px;
    font-size: 0.85rem;
    color: #435666;
}

.setup-banner .fa-google {
    font-size: 1.8rem;
    color: #00a0c1;
    margin-top: 2px;
    flex-shrink: 0;
}

.setup-banner strong {
    display: block;
    margin-bottom: 4px;
    font-size: 0.9rem;
    color: #1a2a35;
}

.setup-banner p {
    margin: 0;
    line-height: 1.5;
}

.setup-banner code {
    background: #daeef5;
    padding: 1px 5px;
    border-radius: 4px;
    font-family: monospace;
    font-size: 0.82rem;
}

/* Controls */
.controls {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
}

.control-group {
    display: flex;
    align-items: center;
    gap: 8px;
}

.control-group label {
    color: #6c757d;
    font-size: 0.9rem;
    font-weight: 500;
    white-space: nowrap;
}

.form-select {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 0.75rem center;
    background-size: 16px 12px;
    padding: 8px 40px 8px 16px;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    font-weight: 500;
    background-color: #fff;
}

/* Meetings list */
.meetings-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-height: 380px;
    overflow-y: auto;
    padding: 16px 24px;
}

.meeting-row {
    display: flex;
    align-items: stretch;
    background-color: #fff;
    border-radius: 10px;
    border: 1px solid #e9ecef;
    flex-shrink: 0;
    overflow: hidden;
    transition: box-shadow 0.15s ease;
}

.meeting-row:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
}

.meeting-row.cancelled {
    opacity: 0.6;
    border-color: #f5c6cb;
    background: #fff5f5;
}

.meeting-date {
    background-color: #435666;
    color: #fff;
    min-width: 130px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 12px;
}

.date-formatted {
    font-weight: 700;
    font-size: 0.9rem;
}

.time-formatted {
    font-size: 0.85rem;
    color: #e9ecef;
}

.meeting-info {
    flex-grow: 1;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
}

.meeting-name {
    font-weight: 700;
    color: #333;
}

.meeting-contact {
    color: #666;
    font-size: 0.88rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* BDM column */
.meeting-bdm {
    min-width: 120px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 12px 12px;
    border-left: 1px solid #e9ecef;
}

.bdm-label {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #aaa;
    font-weight: 600;
}

.bdm-name {
    font-size: 0.88rem;
    font-weight: 600;
    color: #435666;
}

/* Status badge */
.meeting-status {
    display: flex;
    align-items: center;
    padding: 0 16px 0 8px;
}

.status-badge {
    font-size: 0.75rem;
    font-weight: 600;
    padding: 3px 10px;
    border-radius: 12px;
    text-transform: capitalize;
}

.status-badge.scheduled {
    background: #d4edda;
    color: #155724;
}

.status-badge.cancelled {
    background: #f8d7da;
    color: #721c24;
}

/* Pagination */
.pagination {
    display: inline-flex;
    align-items: center;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    background-color: #fff;
    overflow: hidden;
}

.pagination-label {
    font-size: 0.9rem;
    color: #6c757d;
    font-weight: 500;
    padding: 0 8px;
    white-space: nowrap;
}

.pagination-label strong { color: #343a40; }

.pagination-arrow {
    background-color: transparent;
    border: none;
    cursor: pointer;
    color: #00A0C1;
    font-size: 1rem;
    padding: 8px 12px;
    transition: background-color 0.2s ease;
}

.pagination-arrow:hover:not(:disabled) { background-color: #f1f3f5; }
.pagination-arrow:disabled { color: #ced4da; cursor: not-allowed; }

/* Empty / Loading states */
.loading-state,
.empty-state {
    text-align: center;
    padding: 40px;
    color: #aaa;
    font-size: 1rem;
}
</style>